// src/services/secureDraftService.js
import api from './api';

class SecureDraftService {
  constructor() {
    this.sessionDrafts = new Map(); // Memory storage only
    this.debounceTimers = new Map();
    this.DEBOUNCE_DELAY = 3000; // 3 seconds
    this.SESSION_TIMEOUT = 30 * 60 * 1000; // 30 minutes

    this.setupCleanup();
    this.setupBeforeUnloadHandler();
  }

  // Initialize for current session
  initialize(userId, sessionToken) {
    this.userId = userId;
    this.sessionToken = sessionToken;
    this.sessionStartTime = Date.now();
    this.clearMemoryDrafts();
  }

  // Create secure session key
  getSessionKey(sessionId, formType) {
    return `${this.userId}_${sessionId}_${formType}_${this.sessionStartTime}`;
  }

  // Map your form types to match backend
  getBackendFormType(formType) {
    const mapping = {
      'consent': 'consent',
      'intake': 'intake',
      'mentalStatus': 'mentalStatus',
      'record': 'record',
      'safety': 'safety',
      'termination': 'termination'
    };
    return mapping[formType] || formType;
  }

  // Save draft in memory only
  saveDraftInMemory(sessionId, formType, formData) {
    const key = this.getSessionKey(sessionId, formType);
    this.sessionDrafts.set(key, {
      data: formData,
      timestamp: Date.now(),
      sessionId,
      formType
    });

    console.log(`Draft saved in memory: ${formType}`);
  }

  // Get draft from memory
  getDraftFromMemory(sessionId, formType) {
    const key = this.getSessionKey(sessionId, formType);
    const draft = this.sessionDrafts.get(key);

    if (!draft) return null;

    // Check if draft is too old
    if (Date.now() - draft.timestamp > this.SESSION_TIMEOUT) {
      this.sessionDrafts.delete(key);
      return null;
    }

    return draft.data;
  }

  // Count filled fields for metadata
  countFilledFields(obj, count = 0) {
    if (!obj || typeof obj !== 'object') return count;

    for (const key in obj) {
      if (typeof obj[key] === 'object' && obj[key] !== null && !Array.isArray(obj[key])) {
        count = this.countFilledFields(obj[key], count);
      } else if (obj[key] && obj[key] !== '' && obj[key] !== null) {
        if (Array.isArray(obj[key])) {
          count += obj[key].filter(item => item && item !== '').length;
        } else {
          count++;
        }
      }
    }
    return count;
  }

  // Get field names (for metadata)
  getFieldNames(obj, prefix = '') {
    const fields = [];
    if (!obj || typeof obj !== 'object') return fields;

    for (const key in obj) {
      const fieldName = prefix ? `${prefix}.${key}` : key;

      if (typeof obj[key] === 'object' && obj[key] !== null && !Array.isArray(obj[key])) {
        fields.push(...this.getFieldNames(obj[key], fieldName));
      } else if (obj[key] && obj[key] !== '' && obj[key] !== null) {
        fields.push(fieldName);
      }
    }
    return fields;
  }

  // Save metadata to server
  async saveDraftToServer(sessionId, formType, formData) {
    const draftKey = this.getSessionKey(sessionId, formType);

    if (this.debounceTimers.has(draftKey)) {
      clearTimeout(this.debounceTimers.get(draftKey));
    }

    const timer = setTimeout(async () => {
      try {
        const backendFormType = this.getBackendFormType(formType);

        await api.post('/drafts/save-metadata', {
          sessionId,
          formType: backendFormType,
          hasData: Object.keys(formData).length > 0,
          lastModified: Date.now(),
          fieldCount: this.countFilledFields(formData),
          formFields: this.getFieldNames(formData),
          data: formData
        });

        console.log(`Draft metadata saved: ${formType}`);
      } catch (error) {
        console.warn('Failed to save draft metadata:', error);
      } finally {
        this.debounceTimers.delete(draftKey);
      }
    }, this.DEBOUNCE_DELAY);

    this.debounceTimers.set(draftKey, timer);
  }

  // Auto-save (memory + server metadata)
  autoSaveDraft(sessionId, formType, formData) {
    if (!sessionId || !formType || !formData) return;

    // Save in memory for immediate access
    this.saveDraftInMemory(sessionId, formType, formData);

    // Save metadata to server
    this.saveDraftToServer(sessionId, formType, formData);
  }

  // Get draft (memory only)
  getDraft(sessionId, formType) {
    return this.getDraftFromMemory(sessionId, formType);
  }

  // Check if draft exists
  hasDraft(sessionId, formType) {
    return !!this.getDraftFromMemory(sessionId, formType);
  }

  // Get all drafts in current session
  getAllSessionDrafts() {
    const drafts = {};

    for (const [key, draft] of this.sessionDrafts.entries()) {
      if (key.startsWith(`${this.userId}_`)) {
        drafts[draft.formType] = draft.data;
      }
    }

    return drafts;
  }

  // Clear specific draft
  clearDraft(sessionId, formType) {
    const key = this.getSessionKey(sessionId, formType);
    this.sessionDrafts.delete(key);

    // Clear server metadata
    const backendFormType = this.getBackendFormType(formType);
    api.delete(`/drafts/metadata/${sessionId}/${backendFormType}`)
      .catch(error => console.warn('Failed to clear server metadata:', error));
  }

  // Clear all drafts from memory
  clearMemoryDrafts() {
    this.sessionDrafts.clear();

    // Clear all timers
    for (const timer of this.debounceTimers.values()) {
      clearTimeout(timer);
    }
    this.debounceTimers.clear();
  }

  // Setup automatic cleanup
  setupCleanup() {
    setInterval(() => {
      const now = Date.now();
      const keysToDelete = [];

      for (const [key, draft] of this.sessionDrafts.entries()) {
        if (now - draft.timestamp > this.SESSION_TIMEOUT) {
          keysToDelete.push(key);
        }
      }

      keysToDelete.forEach(key => this.sessionDrafts.delete(key));

      if (keysToDelete.length > 0) {
        console.log(`Cleaned up ${keysToDelete.length} expired drafts`);
      }
    }, 5 * 60 * 1000); // Every 5 minutes
  }

  // Setup beforeunload handler
  setupBeforeUnloadHandler() {
    window.addEventListener('beforeunload', () => {
      this.flushPendingSaves();
    });

    document.addEventListener('visibilitychange', () => {
      if (document.hidden) {
        this.flushPendingSaves();
      }
    });
  }

  // Flush pending saves
  flushPendingSaves() {
    for (const [key, timer] of this.debounceTimers.entries()) {
      clearTimeout(timer);

      // Execute save immediately
      const [userId, sessionId, formType] = key.split('_');
      if (userId === this.userId) {
        const draft = this.sessionDrafts.get(key);
        if (draft) {
          // Execute save without debounce
          this.saveDraftToServer(sessionId, formType, draft.data);
        }
      }
    }
  }

  // Emergency clear (for logout)
  emergencyClear() {
    this.clearMemoryDrafts();

    api.delete('/drafts/clear-all-metadata')
      .catch(error => console.warn('Failed to clear server metadata:', error));
  }

  // Get draft statistics
  async getDraftStatistics() {
    try {
      const response = await api.get('/drafts/statistics');
      return response.data?.data || { incompleteForms: 0, lastActivity: null };
    } catch (error) {
      console.warn('Failed to get draft statistics:', error);
      return { incompleteForms: 0, lastActivity: null };
    }
  }

  // Get draft metadata from server
  async getDraftMetadata(sessionId, formType) {
    try {
      const backendFormType = this.getBackendFormType(formType);
      const response = await api.get(`/drafts/metadata/${sessionId}/${backendFormType}`);
      return response.data?.data || null;
    } catch (error) {
      // Draft not found or expired
      return null;
    }
  }

  // Check if there are any drafts for current user
  async hasAnyDrafts() {
    try {
      const response = await api.get('/drafts/user/all-metadata');
      return response.data?.data?.count > 0;
    } catch (error) {
      return false;
    }
  }
  // Restore drafts from server
  async restoreDraftsFromServer(sessionId) {
    if (!sessionId) return {};

    try {
      // Get list of available drafts
      const response = await api.get('/drafts/user/all-metadata');
      const allDrafts = response.data?.data?.drafts || [];

      // Filter for current session
      const sessionDrafts = allDrafts.filter(d => d.sessionId === sessionId);

      const restoredData = {};

      // Fetch full content for each draft
      for (const draft of sessionDrafts) {
        try {
          // Determine frontend form type from backend type
          // (Simple reverse mapping or use provided type if valid)
          const formType = draft.formType;

          const detailResponse = await api.get(`/drafts/metadata/${sessionId}/${formType}`);
          const draftData = detailResponse.data?.data?.data; // The actual form data payload

          if (draftData) {
            // Restore to memory
            this.saveDraftInMemory(sessionId, formType, draftData);
            restoredData[formType] = draftData;
          }
        } catch (err) {
          console.warn(`Failed to restore draft ${draft.formType}`, err);
        }
      }

      return restoredData;
    } catch (error) {
      console.warn('Failed to restore drafts from server:', error);
      return {};
    }
  }
}

export const secureDraftService = new SecureDraftService();
export default secureDraftService;